import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp, faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { LabraToolTipV2 } from '@labrav/react-components'
import { numberFormat } from '@labrav/common'
import { Dimension, Price } from '../../types/flyout'

export type RenewalPricingFormValues = {
  dimensions?: Dimension[]
  tcv?: number
  flexiblePaymentTerms?: boolean
  renewalCount?: number
  renewalPricingTerms?: string
  renewalCountType?: string
  optOutDeadlineDays?: number | string
  priceUpliftType?: string
  priceUpliftPercent?: number | string
  priceUpliftMinPercent?: number | string
  priceUpliftMaxPercent?: number | string
  defaultUpliftPercent?: number | string
  adjustmentDeadlineDays?: number | string
}

export const RenewalPricingTermsOption = {
  NO_PRICE_UPLIFT: 'NO_PRICE_UPLIFT',
  PRICE_UPLIFT: 'PRICE_UPLIFT',
  NO_RENEWAL: 'NO_RENEWAL',
} as const

export const RenewalCountOption = {
  DEFINITE: 'DEFINITE',
  INDEFINITE: 'INDEFINITE',
} as const

export const PriceUpliftTypeOption = {
  FIXED: 'FIXED',
  RANGE: 'RANGE',
} as const

export const renewalPricingTermsOptions = [
  {
    label: 'No price uplift',
    value: RenewalPricingTermsOption.NO_PRICE_UPLIFT,
  },
  { label: 'Price uplift', value: RenewalPricingTermsOption.PRICE_UPLIFT },
  { label: 'No renewal', value: RenewalPricingTermsOption.NO_RENEWAL },
]

export const renewalCountOptions = [
  { label: 'Definite renewal', value: RenewalCountOption.DEFINITE },
  { label: 'Indefinite renewal', value: RenewalCountOption.INDEFINITE },
]

export const priceUpliftTypeOptions = [
  { label: 'Fixed percentage', value: PriceUpliftTypeOption.FIXED },
  { label: 'Percentage range', value: PriceUpliftTypeOption.RANGE },
]

export const RENEWAL_HEADING_ICON_STYLE = {
  width: '18px',
  height: '14px',
} as const

export const RENEWAL_INFO_ICON_STYLE = {
  height: '16px',
  width: '16px',
  fontSize: '16px',
} as const

export const RENEWAL_NUMBER_KEYS_TO_PREVENT = ['+', '-', 'e', 'E']
export const RENEWAL_EMPTY_CELL = '-'
export const DEFAULT_RENEWAL_COLUMNS = 2
export const RENEWAL_INSTALLMENTS_SECTION_INFO =
  'Generate installment plan for the buyer based on total contract value and duration.'
const DESCRIPTION_MAX_LENGTH = 14

export const RENEWAL_PRICING_INFO_TOOLTIPS = {
  section:
    'Configure pricing adjustments and payments schdedule for renewal cycles. All other offer terms, including dimensions, duration, EULA, and buyers, carry over from the original offer.',
  renewalPricingTerms:
    'No price uplift - Renew at the same price each cycle. Price uplift - Increase pricing by a fixed percentage or within a range each cycle. No renewal - Do not include auto-renewal terms with this offer.',
  renewalCount:
    'Definite renewal - Set a specific number of times this contract will renew before expiring. Indefinite renewal - Contract renews continuously until either party opts out.',
  numberOfRenewals:
    'Enter the total numbers of times this contract will automatically renew before expiring.',
  optOutDeadline:
    'Enter the number of days before the contract end date after which neither party can opt out of the renewal.',
  priceUpliftType:
    'Fixed percentage - Set a single uplift % applied uniformly to all dimensions in next renewal. Percentage range - Enter a minimum % and a maximum %. Before the adjustment deadline, you can apply a fixed percentage in this range from Contract details page.',
  priceUpliftPercent:
    'Enter the percentage increase to apply to all pricing dimensions. The price will increase by this percentage at every renewal. If no percentage is entered before the adjustment deadline, the default percentage will be used as the final uplift percentage.',
  adjustmentDeadline:
    'Number of days before opt out date by which you must finalize uplift %. After this deadline, default % is applied automatically.',
  defaultUpliftPercent:
    'If you do not finalize uplift % before Adjustment deadline then default % is applied automatically.',
  productDimensionsTable:
    'Product dimension pricing with the configured uplift applied to the all renewal cycle.',
  usageDimensionsTable:
    'Usage dimension pricing with the configured uplift applied to the all renewal cycle.',
} as const

export const fieldInfoTooltip = (content: string) => ({ title: '', content })

export const RenewalSectionInfoIcon = ({
  description,
  testId,
  className,
}: {
  description: string
  testId: string
  className?: string
}) => (
  <LabraToolTipV2
    tooltipPlacement="top-start"
    description={description}
    tooltipPlacmentGap="13px"
  >
    <FontAwesomeIcon
      icon={faCircleInfo}
      style={RENEWAL_INFO_ICON_STYLE}
      className={className}
      data-testid={testId}
    />
  </LabraToolTipV2>
)

export const ordinalLabel = (n: number) => {
  const suffixes = ['th', 'st', 'nd', 'rd'] as const
  const v = n % 100
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`
}

export const getBasePrice = (price?: Price): number | undefined => {
  if (typeof price === 'number') return price
  if (price && typeof price === 'object') {
    if (typeof price.price === 'number') return price.price
    if (typeof price.additionalUsagePrice === 'number') {
      return price.additionalUsagePrice
    }
    if (typeof price.subscriptionPrice === 'number') {
      return price.subscriptionPrice
    }
  }
  return undefined
}

export const getDimensionBasePrice = (
  dimension: Dimension
): number | undefined => {
  const fromPrice = getBasePrice(dimension.price ?? dimension.prices)
  if (fromPrice !== undefined) return fromPrice
  if (typeof dimension.additionalUsagePrice === 'number') {
    return dimension.additionalUsagePrice
  }
  return undefined
}

export const formatRenewalMoney = (amount: number) =>
  `$${numberFormat(Math.round(amount * 100) / 100)}`

export const truncateRenewalDescription = (name: string) =>
  name === RENEWAL_EMPTY_CELL || name.length <= DESCRIPTION_MAX_LENGTH
    ? name
    : `${name.slice(0, DESCRIPTION_MAX_LENGTH)}...`

type RenewalTableStyles = {
  renewalPrice: string
  upliftBadge: string
  upliftArrow: string
}

export const buildDimensionsRenewalTable = ({
  dimensions,
  renewalColumnCount,
  upliftPercent,
  classes,
}: {
  dimensions: Dimension[]
  renewalColumnCount: number
  upliftPercent: number | null
  classes: RenewalTableStyles
}) => {
  const renewalKeys = Array.from(
    { length: renewalColumnCount },
    (_, i) => `renewal${i + 1}`
  )
  const mapping: Record<string, string> = {
    Dimension: 'label',
    Description: 'desc',
    Price: 'price',
    ...Object.fromEntries(
      renewalKeys.map((key, i) => [`${ordinalLabel(i + 1)} renewal price`, key])
    ),
  }

  const objects = dimensions.map((dimension: Dimension) => {
    const basePrice = getDimensionBasePrice(dimension)
    const name = dimension.name || RENEWAL_EMPTY_CELL
    const row: Record<string, string | JSX.Element> = {
      label: dimension.label || RENEWAL_EMPTY_CELL,
      desc: truncateRenewalDescription(name),
      price:
        basePrice === undefined
          ? RENEWAL_EMPTY_CELL
          : formatRenewalMoney(basePrice),
    }

    renewalKeys.forEach((key, i) => {
      if (basePrice === undefined || upliftPercent === null) {
        row[key] = RENEWAL_EMPTY_CELL
        return
      }
      const amount = basePrice * Math.pow(1 + upliftPercent / 100, i + 1)
      row[key] = (
        <span className={classes.renewalPrice}>
          {formatRenewalMoney(amount)}
          <span className={classes.upliftBadge}>
            <FontAwesomeIcon icon={faArrowUp} className={classes.upliftArrow} />
            {`${upliftPercent}%`}
          </span>
        </span>
      )
    })

    return row
  })

  return { mapping, objects }
}

export const resolveUpliftPercent = ({
  priceUpliftType,
  priceUpliftPercent,
  defaultUpliftPercent,
}: {
  priceUpliftType: string
  priceUpliftPercent?: number | string
  defaultUpliftPercent?: number | string
}): number | null => {
  if (priceUpliftType === PriceUpliftTypeOption.RANGE) {
    if (
      defaultUpliftPercent === undefined ||
      Number.isNaN(Number(defaultUpliftPercent))
    ) {
      return null
    }
    return Number(defaultUpliftPercent)
  }
  if (priceUpliftType !== PriceUpliftTypeOption.FIXED) return null
  if (
    priceUpliftPercent === undefined ||
    Number.isNaN(Number(priceUpliftPercent))
  ) {
    return null
  }
  return Number(priceUpliftPercent)
}

/** No price uplift → 0%; fixed % or range default otherwise. */
export const resolveSeedUpliftPercent = (
  values: RenewalPricingFormValues
): number => {
  if (values.renewalPricingTerms !== RenewalPricingTermsOption.PRICE_UPLIFT) {
    return 0
  }
  return (
    resolveUpliftPercent({
      priceUpliftType: values.priceUpliftType ?? PriceUpliftTypeOption.FIXED,
      priceUpliftPercent: values.priceUpliftPercent,
      defaultUpliftPercent: values.defaultUpliftPercent,
    }) ?? 0
  )
}
